import * as amplitude from "@amplitude/analytics-browser";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { hasReturnBear } from "@redotech/redo-model/integration/returnbear/returnbear-utils";
import { MerchantAppReturn, Return } from "@redotech/redo-model/return";
import { toast } from "@redotech/redo-web/alert";
import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";

function getErrorMessage(err: unknown): string {
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return "Failed to send email. Please contact our support team to resolve this issue.";
}

export interface ResendLabelVariables {
  emailOverride: string;
}

export function useResendLabel(return_: Return | MerchantAppReturn) {
  const merchantRpcClient = useContext(RedoMerchantRpcClientContext);
  const teamContext = useContext(TeamContext);

  return useMutation({
    mutationFn: async ({ emailOverride }: ResendLabelVariables) => {
      try {
        const hasBlueYonderReturns =
          !!return_.blueYonder?.returns &&
          return_.blueYonder.returns.length > 0;
        const isExternalReturnShippingProvider =
          hasReturnBear(return_) || hasBlueYonderReturns;

        amplitude.logEvent(
          isExternalReturnShippingProvider
            ? "resend-external-return-shipping-provider-label"
            : "resend-label-support",
          { team: teamContext?._id, returnId: return_._id },
        );

        if (isExternalReturnShippingProvider && merchantRpcClient) {
          const result =
            await merchantRpcClient.resendExternalReturnShippingProviderLabel({
              returnId: return_._id,
              emailOverride: emailOverride || undefined,
            });

          if (!result.success) {
            throw new Error(
              result.message || "Failed to send shipping label email",
            );
          }
          return;
        }

        if (!merchantRpcClient) {
          throw new Error("RPC client not available");
        }

        const result = await merchantRpcClient.resendLabel({
          returnId: return_._id,
          emailOverride: emailOverride || undefined,
        });

        if (!result.success) {
          throw new Error(
            result.message || "Failed to send shipping label email",
          );
        }
      } catch (err) {
        throw new Error(getErrorMessage(err));
      }
    },
    onSuccess: () => {
      toast("Label sent successfully!", { variant: "success" });
    },
    onError: (error) => {
      const message =
        error instanceof Error && error.message
          ? `Resending label failed: ${error.message}`
          : "Resending label failed";
      toast(message, { variant: "error" });
    },
  });
}
